import { useReviewPopup } from '@context/reviewPopupProvider';
import { buildCreateModeConfig } from '@lib/createModeConfig';
import { switchDisplayAt } from '@lib/displayAtOptions';
import { parseTimerType } from '@lib/timerTypes';
import { useRouter } from 'next/router';
import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useConfigSettings } from '@/hooks/useConfigSettings';
import { ConfigData, Route } from '@/types';

interface SettingsContextProps {
	settings: ConfigData;
	localConfigData: ConfigData;
	configId: number | undefined;
	isEditMode: boolean;
	isSettingsLoading: boolean;
	updateSettings: (updatedConfig: Partial<ConfigData>) => void;
	changeDisplayAt: (displayAt: string) => void;
	saveSettings: () => Promise<void>;
	isLoading: boolean;
}

const SettingsContext = createContext<SettingsContextProps | undefined>(undefined);

export function useSettings(): SettingsContextProps {
	const context = useContext(SettingsContext);
	if (!context) {
		throw new Error('useSettings must be used within a SettingsProvider');
	}

	return context;
}

interface SettingsProviderProps {
	tid: string | undefined;
	children: ReactNode;
	onLoad?: () => void;
	onError?: (error: string) => void;
}

const parseTimerId = (tid: string | undefined) =>
	tid && !Number.isNaN(Number(tid)) ? Number.parseInt(tid, 10) : undefined;

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ tid, children, onLoad, onError }) => {
	const router = useRouter();
	const activeTid = parseTimerId(tid);
	/** Set by the creation wizard: `/create?timerType=…&template=…`. */
	const createTimerType = parseTimerType(router.query.timerType);
	const createTemplateId = typeof router.query.template === 'string' ? router.query.template : undefined;

	const editorScope = `${router.pathname}:${activeTid ?? ''}:${createTimerType}:${createTemplateId ?? ''}`;

	const { configData, isLoading, error, saveSettings: saveSettingsToServer } = useConfigSettings(activeTid);
	const { notifyTimerSaved } = useReviewPopup();

	const [isSaving, setIsSaving] = useState(false);
	const [draft, setDraft] = useState<ConfigData | null>(null);
	const [draftScope, setDraftScope] = useState<string>(editorScope);
	const createModeConfig = useMemo(
		() => buildCreateModeConfig(createTimerType, createTemplateId),
		[createTimerType, createTemplateId],
	);

	const mode = activeTid ? Route.EDIT : Route.CREATE;
	const isEditMode = mode === Route.EDIT;
	const isCreateMode = !isEditMode;
	const serverConfig = isEditMode ? configData : createModeConfig;
	const isSettingsLoading = isEditMode && !serverConfig;
	const localConfigData = draft ?? serverConfig ?? createModeConfig;
	const settings = serverConfig ?? createModeConfig;

	if (draftScope !== editorScope) {
		setDraftScope(editorScope);
		setDraft(null);
	}

	useEffect(() => {
		if (isEditMode && error && onError) {
			onError('Failed to load settings. Please try again.');
		}
	}, [onError, isEditMode, error]);

	useEffect(() => {
		if (!onLoad) {
			return;
		}

		if (isCreateMode) {
			onLoad();

			return;
		}

		if (!isLoading && !error) {
			onLoad();
		}
	}, [onLoad, isCreateMode, isLoading, error]);

	const getChangedFields = (original: ConfigData, updated: ConfigData): Partial<ConfigData> => {
		return Object.fromEntries(
			Object.entries(updated).filter(([key, value]) => original[key as keyof ConfigData] !== value),
		) as Partial<ConfigData>;
	};

	const updateSettings = (updatedConfig: Partial<ConfigData>) => {
		setDraft((prevDraft) => {
			const filteredConfig = Object.fromEntries(
				Object.entries(updatedConfig).filter(([, value]) => value !== undefined),
			) as Partial<ConfigData>;

			return { ...(prevDraft ?? serverConfig ?? createModeConfig), ...filteredConfig };
		});
	};

	const changeDisplayAt = (displayAt: string) => {
		updateSettings(switchDisplayAt(localConfigData, displayAt));
	};

	const saveSettings = async () => {
		setIsSaving(true);

		try {
			if (isEditMode) {
				if (!activeTid) {
					throw new Error('Timer ID is missing.');
				}

				const updatedConfig = configData ? getChangedFields(configData, localConfigData) : localConfigData;

				if (Object.keys(updatedConfig).length === 0) {
					toast.info('No changes to save.');

					return;
				}

				const { isSaved } = await saveSettingsToServer(updatedConfig, activeTid);

				if (isSaved) {
					notifyTimerSaved();
				}

				return;
			}

			const { isSaved, id } = await saveSettingsToServer(localConfigData);

			if (isSaved) {
				notifyTimerSaved();
			}

			if (id) {
				await router.replace(`/edit/${id}`);
			}
		} catch (saveError) {
			toast.error((saveError as Error).message ?? 'An unexpected error occurred.');
		} finally {
			setIsSaving(false);
		}
	};

	return (
		<SettingsContext.Provider
			value={{
				settings,
				localConfigData,
				configId: activeTid,
				isEditMode,
				isSettingsLoading,
				updateSettings,
				changeDisplayAt,
				saveSettings,
				isLoading: isSaving,
			}}
		>
			{children}
		</SettingsContext.Provider>
	);
};
